import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import deliveryApi from '../../../api/deliveryApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';

const schema = yup.object({
  district: yup.string().trim().required('El distrito es obligatorio').max(255),
  price_express: yup
    .number()
    .typeError('Ingresa un precio válido')
    .min(0, 'Debe ser mayor o igual a 0')
    .required('El precio de delivery express es obligatorio'),
  price_normal: yup
    .number()
    .typeError('Ingresa un precio válido')
    .min(0, 'Debe ser mayor o igual a 0')
    .required('El precio de delivery normal es obligatorio'),
  is_active: yup.boolean().default(true),
});

const emptyValues = {
  district: '',
  price_express: '',
  price_normal: '',
  is_active: true,
};

// Formulario de alta/edicion de un distrito de Lima con costo de delivery.
// `district` nulo significa "crear nuevo".
export default function DeliveryDistrictFormDialog({ open, onClose, onSaved, district }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(district);

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(
      district
        ? {
            district: district.district,
            price_express: district.price_express,
            price_normal: district.price_normal,
            is_active: district.is_active,
          }
        : emptyValues
    );
  }, [open, district, reset]);

  const onSubmit = async (data) => {
    try {
      if (isEditing) {
        await deliveryApi.updateDeliveryDistrict(district.id, data);
        enqueueSnackbar('Distrito actualizado correctamente.', { variant: 'success' });
      } else {
        await deliveryApi.createDeliveryDistrict(data);
        enqueueSnackbar('Distrito creado correctamente.', { variant: 'success' });
      }
      onSaved();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar el distrito.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar distrito' : 'Nuevo distrito'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="delivery-district-form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
          {errors.root?.message && <Alert severity="error">{errors.root.message}</Alert>}

          <TextField
            label="Distrito"
            fullWidth
            placeholder="Ej. Miraflores, San Isidro, Surco..."
            error={Boolean(errors.district)}
            helperText={errors.district?.message}
            {...register('district')}
          />

          <TextField
            label="Delivery Express (S/)"
            type="number"
            fullWidth
            slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
            error={Boolean(errors.price_express)}
            helperText={errors.price_express?.message || 'Entrega el mismo día.'}
            {...register('price_express')}
          />

          <TextField
            label="Delivery Normal (S/)"
            type="number"
            fullWidth
            slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
            error={Boolean(errors.price_normal)}
            helperText={errors.price_normal?.message || 'Entrega programada hasta 3 días.'}
            {...register('price_normal')}
          />

          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                label="Distrito activo (visible en el checkout)"
              />
            )}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" form="delivery-district-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DeliveryDistrictFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  district: PropTypes.object,
};

DeliveryDistrictFormDialog.defaultProps = {
  district: null,
};
